import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Box, alpha, useTheme,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Divider, Skeleton, Alert, Paper,
  CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  ReceiptLong, CalendarMonth, School,
  Download as DownloadIcon, Timer, DeleteOutline, MoreHoriz, RestartAlt,
  MoneyOffOutlined, UndoOutlined, SavingsOutlined, LockOutlined, AddCircleOutline, EditNoteOutlined,
} from '@mui/icons-material';
import { format } from 'date-fns';

import feeInvoiceService from '../../services/feeInvoiceService';
import feeWaiverService from '../../services/feeWaiverService';
import WaiveInvoiceDialog from './WaiveInvoiceDialog';
import ReasonDialog from '../common/ReasonDialog';
import FeeRequestDialog from '../finance/requests/FeeRequestDialog';
import ChallanAdjustmentDialog, { ChallanAdjustmentMode } from './ChallanAdjustmentDialog';
import financeService from '../../services/financeService';
import { FEE_REQUEST_META, FeeRequest, FeeRequestKind, FeeRequestLimits } from '../../types/finance';
import { elegantMenuWithHintsProps } from '../../utils/menuStyles';
import {
  FeeInvoiceDetail, FEE_INVOICE_STATUS_META, ADJUSTMENT_KIND_LABEL,
} from '../../types/fees';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useSnackbar } from 'notistack';
import { formatChallanNumber } from '../../utils/challanNumber';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { useFeeVoucherPdf } from './useFeeVoucherPdf';
import { useSchoolBranding } from '../../utils/pdf';

interface Props {
  open: boolean;
  invoiceId: string | null;
  onClose: () => void;
  /** Fired after anything on the challan changes, so the host list can refresh. */
  onChanged?: () => void;
}

const nameOf = (v: any): string | null => {
  if (!v) return null;
  if (typeof v === 'string') return null;
  return v.name ?? null;
};

const InvoiceDetailDialog: React.FC<Props> = ({ open, invoiceId, onClose, onChanged }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  // Adjustments (ad-hoc / late-fee revoke) sit on `fees.write` because they
  // parallel invoice cancellation. Waivers get their own scope so approving
  // a "give this back" is a distinct grant (audit finding P1-7).
  const canWriteFees    = canWrite('fees');
  const canWriteWaivers = canWrite('fee_waivers');
  const seesMoneyRequests = canRead('fee_waivers') || canRead('fee_refunds') || canRead('fee_writeoffs');

  const [invoice, setInvoice] = useState<FeeInvoiceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { branding } = useSchoolBranding();
  const vouchers = useFeeVoucherPdf(branding);
  const downloading = !!invoiceId && vouchers.isBusy(invoiceId);
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [waivePrefill, setWaivePrefill] = useState<{ amount: number; reason: string } | null>(null);

  // Revoking a waiver or adjustment records why (it reverses a ledger entry).
  const [revoking, setRevoking] = useState<{ type: 'waiver' | 'adjustment'; id: string; label: string; writeOff: boolean } | null>(null);
  const [limits, setLimits] = useState<FeeRequestLimits | null>(null);
  const [requests, setRequests] = useState<FeeRequest[]>([]);
  // Money given back on this challan (posted refunds and returned deposits).
  const [refunded, setRefunded] = useState(0);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [requestKind, setRequestKind] = useState<FeeRequestKind | null>(null);
  const [adjusting, setAdjusting] = useState<ChallanAdjustmentMode | null>(null);

  // What may be written off / refunded here, and requests already raised.
  const loadMoneyRequests = async (id: string) => {
    if (!seesMoneyRequests) return;
    try {
      const [l, r, posted] = await Promise.all([
        financeService.getFeeRequestLimits(id),
        financeService.listFeeRequests({ invoiceId: id, status: 'PENDING', limit: 20 }),
        financeService.listFeeRequests({ invoiceId: id, status: 'POSTED', limit: 50 }),
      ]);
      setLimits(l);
      setRequests(r.items);
      setRefunded(posted.items.filter(x => FEE_REQUEST_META[x.kind].paysOut).reduce((s, x) => s + x.amount, 0));
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not load waivers, refunds and write-offs for this challan', { variant: 'error' });
    }
  };

  const refreshInvoice = async () => {
    if (!invoiceId) return;
    try {
      const fresh = await feeInvoiceService.getFeeInvoice(invoiceId);
      setInvoice(fresh);
      onChanged?.();
      await loadMoneyRequests(invoiceId);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to reload challan', { variant: 'error' });
    }
  };

  const handleDownload = () => {
    if (invoiceId) vouchers.download(invoiceId, () => feeInvoiceService.getInvoicePrint(invoiceId));
  };


  useEffect(() => {
    if (!open || !invoiceId) {
      setInvoice(null);
      setError(null);
      setLimits(null);
      setRequests([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await feeInvoiceService.getFeeInvoice(invoiceId);
        if (!cancelled) setInvoice(data);
        if (!cancelled) await loadMoneyRequests(invoiceId);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load challan');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceId]);

  const currencyFmt = useCurrencyFormat();
  // Charges read +PKR 250, reductions −PKR 100.
  const signed = (n: number) => `${n >= 0 ? '+' : '−'}${currencyFmt(Math.abs(n))}`;

  const toneStyle = (tone: 'success' | 'warning' | 'error' | 'info' | 'neutral') => {
    const palette = tone === 'neutral' ? theme.palette.text : theme.palette[tone];
    const base = (palette as any).main ?? theme.palette.text.primary;
    const dark = (palette as any).dark ?? base;
    return {
      borderColor: alpha(base, 0.32),
      color: dark,
      bgcolor: alpha(base, 0.08),
    };
  };

  // Split the invoice's active adjustments into late-fee vs everything else so
  // the totals row can call each out by its real name ("Late fees" vs
  // "Adjustments") instead of one generic bucket. Section heading follows
  // the same rule.
  const adjustmentBreakdown = useMemo(() => {
    const list = (invoice?.adjustments ?? []).filter(a => Number(a.amount) !== 0);
    const money = (n: number) => Math.round(n * 100) / 100;
    let lateFeeTotal = 0;
    let otherTotal = 0;
    let writeOffTotal = 0;
    let lateFeeCount = 0;
    let otherCount = 0;
    for (const a of list) {
      if (a.kind === 'late_fee') { lateFeeTotal += Number(a.amount); lateFeeCount += 1; }
      else if (a.kind === 'write_off') { writeOffTotal += Number(a.amount); otherCount += 1; }
      else { otherTotal += Number(a.amount); otherCount += 1; }
    }
    const sectionTitle = lateFeeCount > 0 && otherCount === 0
      ? 'Late fees'
      : lateFeeCount === 0 && otherCount > 0
        ? 'Adjustments'
        : 'Late fees & adjustments';
    return {
      list,
      lateFeeTotal: money(lateFeeTotal),
      otherTotal:   money(otherTotal),
      writeOffTotal: money(writeOffTotal),
      sectionTitle,
    };
  }, [invoice?.adjustments]);

  // Write-off, refund and deposit actions this person may take on this challan.
  const moreActions: Array<{ kind: FeeRequestKind; icon: React.ReactNode; hint: string }> = (!invoice || !limits ? [] : [
    ...(canWrite('fee_writeoffs') && limits.writeOff.max > 0
      ? [{ kind: 'WRITE_OFF' as const, icon: <MoneyOffOutlined />, hint: limits.writeOff.eligible ? `Up to ${currencyFmt(limits.writeOff.max)}` : limits.writeOff.reason }] : []),
    ...(canWrite('fee_refunds') && limits.refund > 0
      ? [{ kind: 'REFUND' as const, icon: <UndoOutlined />, hint: `Up to ${currencyFmt(limits.refund)} of fees paid` }] : []),
    ...(canWrite('fee_refunds') && limits.deposit > 0
      ? [
        { kind: 'DEPOSIT_REFUND' as const, icon: <SavingsOutlined />, hint: `Up to ${currencyFmt(limits.deposit)}` },
        { kind: 'DEPOSIT_RETAIN' as const, icon: <LockOutlined />, hint: 'The school keeps it; no money moves' },
      ] : []),
  ]);

  // Changing what the challan asks for: an extra charge, or a correction up or down.
  const adjustActions: Array<{ mode: ChallanAdjustmentMode; icon: React.ReactNode; label: string; hint: string }> =
    canWriteFees && invoice && invoice.status !== 'cancelled' ? [
      { mode: 'charge', icon: <AddCircleOutline />, label: 'Add a charge', hint: 'Something extra, e.g. a replacement ID card' },
      { mode: 'correction', icon: <EditNoteOutlined />, label: 'Correct the amount', hint: 'It was billed wrongly — reduce or raise it' },
    ] : [];

  const isOverdue = invoice
    ? (invoice.status === 'issued' || invoice.status === 'partial') && new Date(invoice.dueDate) < new Date()
    : false;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <ReceiptLong color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            {invoice ? `Challan ${formatChallanNumber(invoice.invoiceNumber)}` : 'Challan'}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading && (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={80} />
            <Skeleton variant="rounded" height={160} />
            <Skeleton variant="rounded" height={80} />
          </Stack>
        )}

        {error && !loading && (
          <Alert severity="error" variant="outlined">{error}</Alert>
        )}

        {invoice && !loading && (
          <Stack spacing={2}>
            {/* Header block: student, cohort, plan, period */}
            <Box sx={{
              p: 2, borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {nameOf(invoice.studentId) ?? nameOf(invoice.personId) ?? '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    <School fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    {nameOf(invoice.classId) ?? '—'}
                    {invoice.sectionInstanceId && ` – ${nameOf(invoice.sectionInstanceId) ?? '—'}`}
                    {typeof invoice.enrollmentId === 'object' && invoice.enrollmentId?.rollNumber != null && (
                      <> · Roll {invoice.enrollmentId.rollNumber}</>
                    )}
                  </Typography>
                  {invoice.feePlanId && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Plan: {nameOf(invoice.feePlanId) ?? '—'} · Session: {nameOf(invoice.sessionId) ?? '—'}
                    </Typography>
                  )}
                </Box>
                <Stack alignItems="flex-end" spacing={0.5}>
                  <Chip
                    size="small"
                    label={FEE_INVOICE_STATUS_META[invoice.status].label}
                    variant="outlined"
                    sx={{ ...toneStyle(FEE_INVOICE_STATUS_META[invoice.status].tone), fontWeight: 600 }}
                  />
                  {isOverdue && (
                    <Chip
                      size="small"
                      label="Overdue"
                      variant="outlined"
                      sx={{ ...toneStyle('error'), fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Period</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <CalendarMonth fontSize="inherit" />
                    <Typography variant="body2" fontWeight={600}>{invoice.period.label}</Typography>
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Due</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {format(new Date(invoice.dueDate), 'MMM d, yyyy')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Issued</Typography>
                  <Typography variant="body2">
                    {format(new Date(invoice.issuedAt), 'MMM d, yyyy')}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Line items */}
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
                Line items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ pl: 0 }}>Category</TableCell>
                    <TableCell align="right">Gross</TableCell>
                    <TableCell align="right">Concession</TableCell>
                    <TableCell align="right">Waiver</TableCell>
                    <TableCell align="right" sx={{ pr: 0 }}>Net</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.lineItems.map((li, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ pl: 0 }}>
                        <Typography variant="body2" fontWeight={500}>{li.categoryName}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{currencyFmt(li.grossAmount)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={li.concessionAmount ? 'text.primary' : 'text.disabled'}>
                          {li.concessionAmount ? `-${currencyFmt(li.concessionAmount)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={li.waivedAmount ? 'text.primary' : 'text.disabled'}>
                          {li.waivedAmount ? `-${currencyFmt(li.waivedAmount)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 0 }}>
                        <Typography variant="body2" fontWeight={600}>{currencyFmt(li.netAmount)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {/* Waivers */}
            {(invoice.waivers && invoice.waivers.length > 0) && (
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
                  Waivers
                </Typography>
                <Stack spacing={0.75}>
                  {invoice.waivers.map((w) => {
                    const lineName = w.lineItemIndex == null ? 'Whole invoice' : (invoice.lineItems[w.lineItemIndex]?.categoryName ?? 'Line');
                    return (
                      <Paper
                        key={w._id}
                        variant="outlined"
                        sx={{
                          p: 1.25, borderRadius: 1.5,
                          borderColor: alpha(theme.palette.warning.main, 0.35),
                          bgcolor: alpha(theme.palette.warning.main, 0.04),
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Box sx={{
                            width: 26, height: 26, borderRadius: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(theme.palette.warning.main, 0.15),
                            color: theme.palette.warning.dark,
                            flexShrink: 0,
                          }}>
                            <RestartAlt sx={{ fontSize: 16 }} />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{lineName}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {w.reason} · {format(new Date(w.createdAt), 'MMM d, yyyy')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={700} sx={{
                            fontVariantNumeric: 'tabular-nums',
                            color: theme.palette.warning.dark,
                            flexShrink: 0,
                          }}>
                            −{currencyFmt(w.amount)}
                          </Typography>
                          {canWriteWaivers && (
                            <Button
                              size="small"
                              color="error"
                              variant="text"
                              startIcon={<DeleteOutline fontSize="small" />}
                              onClick={() => setRevoking({ type: 'waiver', id: w._id, label: `${lineName} — ${currencyFmt(w.amount)}`, writeOff: false })}
                            >
                              Revoke
                            </Button>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Late fees + ad-hoc adjustments. Section heading reflects
                whichever kinds are actually present. */}
            {(invoice.adjustments && invoice.adjustments.length > 0) && (
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
                  {adjustmentBreakdown.sectionTitle}
                </Typography>
                <Stack spacing={0.75}>
                  {invoice.adjustments.map((a) => (
                    <Paper
                      key={a._id}
                      variant="outlined"
                      sx={{
                        p: 1.25, borderRadius: 1.5,
                        borderColor: a.kind === 'late_fee'
                          ? alpha(theme.palette.warning.main, 0.35)
                          : theme.palette.divider,
                        bgcolor: a.kind === 'late_fee'
                          ? alpha(theme.palette.warning.main, 0.04)
                          : 'transparent',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        {a.kind === 'late_fee' && (
                          <Box sx={{
                            width: 26, height: 26, borderRadius: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(theme.palette.warning.main, 0.15),
                            color: theme.palette.warning.dark,
                            flexShrink: 0,
                          }}>
                            <Timer sx={{ fontSize: 16 }} />
                          </Box>
                        )}
                        {a.kind !== 'late_fee' && (
                          <Box sx={{
                            width: 26, height: 26, borderRadius: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(theme.palette.text.primary, 0.06),
                            color: theme.palette.text.secondary,
                            flexShrink: 0,
                          }}>
                            <MoreHoriz sx={{ fontSize: 16 }} />
                          </Box>
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Typography variant="body2" fontWeight={600}>
                              {ADJUSTMENT_KIND_LABEL[a.kind]}
                            </Typography>
                            {a.meta?.daysOverdue != null && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${a.meta.daysOverdue}d late`}
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {a.reason} · {format(new Date(a.createdAt), 'MMM d, yyyy')}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={700} sx={{
                          fontVariantNumeric: 'tabular-nums',
                          color: a.amount >= 0 ? theme.palette.warning.dark : theme.palette.success.dark,
                          flexShrink: 0,
                        }}>
                          {signed(a.amount)}
                        </Typography>
                        {(a.kind === 'write_off' ? canWrite('fee_writeoffs') : canWriteFees) && (
                          <Button
                            size="small"
                            color={a.kind === 'write_off' ? 'primary' : 'error'}
                            variant="text"
                            startIcon={a.kind === 'write_off' ? <UndoOutlined fontSize="small" /> : <DeleteOutline fontSize="small" />}
                            onClick={() => setRevoking({
                              type: 'adjustment',
                              id: a._id,
                              label: `${ADJUSTMENT_KIND_LABEL[a.kind]} — ${signed(a.amount)}`,
                              writeOff: a.kind === 'write_off',
                            })}
                          >
                            {a.kind === 'write_off' ? 'Undo' : 'Revoke'}
                          </Button>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Arrears — prior unpaid challans for the same student. Read-time
                view; matches the PDF renderer so the modal and the printed
                challan tell the parent the same story. Hidden on paid /
                waived / cancelled challans (reprinting old bills would
                misleadingly show today's arrears from other invoices). */}
            {invoice
              && ['issued', 'partial'].includes(invoice.status)
              && (invoice.arrears?.length ?? 0) > 0
              && (() => {
                const arrears = invoice.arrears ?? [];
                const arrearsTotal = Math.round(
                  arrears.reduce((s, a) => s + Number(a.totals?.outstanding ?? 0), 0) * 100,
                ) / 100;
                if (arrearsTotal <= 0) return null;
                return (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2, borderRadius: 2,
                      borderColor: alpha(theme.palette.warning.main, 0.4),
                      bgcolor: alpha(theme.palette.warning.main, 0.05),
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                      <Typography
                        variant="overline"
                        sx={{ color: theme.palette.warning.dark, letterSpacing: 0.8, fontWeight: 700 }}
                      >
                        Arrears · Prior unpaid challans
                      </Typography>
                      <Chip
                        size="small"
                        label={`${arrears.length} pending`}
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: alpha(theme.palette.warning.main, 0.14),
                          color: theme.palette.warning.dark,
                        }}
                      />
                    </Stack>
                    <Stack spacing={0.75}>
                      {arrears.map(a => (
                        <Stack
                          key={a._id}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="baseline"
                          spacing={1}
                        >
                          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {a.period.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: 'monospace',
                                color: 'text.disabled',
                              }}
                            >
                              {a.invoiceNumber}
                            </Typography>
                          </Stack>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              color: theme.palette.warning.dark,
                            }}
                          >
                            {currencyFmt(a.totals.outstanding)}
                          </Typography>
                        </Stack>
                      ))}
                      <Divider sx={{ my: 0.5, borderColor: alpha(theme.palette.warning.main, 0.3) }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography variant="body2" fontWeight={800} sx={{ color: theme.palette.warning.dark }}>
                          Total arrears
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{
                            fontVariantNumeric: 'tabular-nums',
                            color: theme.palette.warning.dark,
                          }}
                        >
                          {currencyFmt(arrearsTotal)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })()}

            {/* Totals */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              {(() => {
                const arrears = invoice.arrears ?? [];
                const arrearsTotal = Math.round(
                  arrears.reduce((s, a) => s + Number(a.totals?.outstanding ?? 0), 0) * 100,
                ) / 100;
                const showGrandTotal =
                  ['issued', 'partial'].includes(invoice.status)
                  && arrearsTotal > 0;
                const grandTotal = Math.round(
                  (invoice.totals.outstanding + arrearsTotal) * 100,
                ) / 100;
                return (
                  <Stack spacing={0.75}>
                    <Row label="Gross" value={currencyFmt(invoice.totals.gross)} />
                    {invoice.totals.concessions > 0 && (
                      <Row label="Concessions" value={`-${currencyFmt(invoice.totals.concessions)}`} />
                    )}
                    {invoice.totals.waivers > 0 && (
                      <Row label="Waivers" value={`-${currencyFmt(invoice.totals.waivers)}`} />
                    )}
                    {adjustmentBreakdown.lateFeeTotal !== 0 && (
                      <Row
                        label="Late fees"
                        value={signed(adjustmentBreakdown.lateFeeTotal)}
                        color={adjustmentBreakdown.lateFeeTotal >= 0 ? theme.palette.warning.dark : theme.palette.success.dark}
                      />
                    )}
                    {adjustmentBreakdown.otherTotal !== 0 && (
                      <Row
                        label="Adjustments"
                        value={signed(adjustmentBreakdown.otherTotal)}
                        color={adjustmentBreakdown.otherTotal >= 0 ? theme.palette.warning.dark : theme.palette.success.dark}
                      />
                    )}
                    {adjustmentBreakdown.writeOffTotal !== 0 && (
                      <Row label="Written off (bad debt)" value={currencyFmt(adjustmentBreakdown.writeOffTotal)} color={theme.palette.text.secondary} />
                    )}
                    <Divider />
                    <Row label="Net" value={currencyFmt(invoice.totals.net)} bold />
                    <Row label="Paid" value={currencyFmt(invoice.totals.paid)} muted={invoice.totals.paid === 0} />
                    {refunded > 0 && <Row label="Refunded to the family" value={`−${currencyFmt(refunded)}`} color={theme.palette.text.secondary} />}
                    <Row
                      label={showGrandTotal ? "This challan's balance" : 'Outstanding'}
                      value={currencyFmt(invoice.totals.outstanding)}
                      bold={!showGrandTotal}
                      muted={showGrandTotal}
                      color={
                        showGrandTotal
                          ? undefined
                          : invoice.totals.outstanding > 0
                            ? theme.palette.warning.dark
                            : theme.palette.success.dark
                      }
                    />
                    {showGrandTotal && (
                      <>
                        <Row
                          label={`Arrears (${arrears.length} prior)`}
                          value={`+${currencyFmt(arrearsTotal)}`}
                          color={theme.palette.warning.dark}
                        />
                        <Divider />
                        <Row
                          label="Grand total payable"
                          value={currencyFmt(grandTotal)}
                          bold
                          color={theme.palette.warning.dark}
                        />
                      </>
                    )}
                  </Stack>
                );
              })()}
            </Paper>

            {requests.length > 0 && (
              <Alert severity="info" variant="outlined" icon={<LockOutlined fontSize="small" />}>
                {requests.map(r => (
                  <Typography key={r._id} variant="body2">
                    Waiting for approval: <strong>{FEE_REQUEST_META[r.kind].label}</strong> of {currencyFmt(r.amount)} — raised by {r.createdByName ?? 'someone'}
                  </Typography>
                ))}
              </Alert>
            )}

            {invoice.status === 'cancelled' && invoice.cancellationReason && (
              <Alert severity="info" variant="outlined">
                Cancellation reason: {invoice.cancellationReason}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Close</Button>
        {moreActions.length + adjustActions.length > 0 && (
          <Button onClick={e => setMoreAnchor(e.currentTarget)} variant="outlined" color="inherit" startIcon={<MoreHoriz fontSize="small" />}>
            More
          </Button>
        )}
        {canWriteWaivers && invoice && invoice.status !== 'cancelled' && invoice.totals.outstanding > 0 && (
          <Button
            onClick={() => setWaiveOpen(true)}
            variant="outlined"
            color="warning"
            startIcon={<RestartAlt fontSize="small" />}
          >
            Waive
          </Button>
        )}
        <Button
          onClick={handleDownload}
          disabled={!invoice || loading || downloading}
          variant="contained"
          disableElevation
          startIcon={
            downloading
              ? <CircularProgress size={14} thickness={5} color="inherit" />
              : <DownloadIcon fontSize="small" />
          }
        >
          {downloading ? 'Preparing…' : 'Download voucher (PDF)'}
        </Button>
      </DialogActions>

      <WaiveInvoiceDialog
        open={waiveOpen}
        invoice={invoice}
        limits={limits}
        prefill={waivePrefill}
        onClose={() => { setWaiveOpen(false); setWaivePrefill(null); }}
        onApplied={refreshInvoice}
      />

      <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)} {...elegantMenuWithHintsProps}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {adjustActions.map(a => (
          <MenuItem key={a.mode} onClick={() => { setAdjusting(a.mode); setMoreAnchor(null); }}>
            <ListItemIcon>{a.icon}</ListItemIcon>
            <ListItemText primary={a.label} secondary={a.hint} />
          </MenuItem>
        ))}
        {adjustActions.length > 0 && moreActions.length > 0 && <Divider sx={{ my: 0.5, mx: 1 }} />}
        {moreActions.map(a => (
          <MenuItem key={a.kind} onClick={() => { setRequestKind(a.kind); setMoreAnchor(null); }}>
            <ListItemIcon>{a.icon}</ListItemIcon>
            <ListItemText primary={FEE_REQUEST_META[a.kind].verb} secondary={a.hint} />
          </MenuItem>
        ))}
      </Menu>

      <ChallanAdjustmentDialog
        mode={adjusting}
        invoice={invoice ? { _id: invoice._id, invoiceNumber: formatChallanNumber(invoice.invoiceNumber), outstanding: invoice.totals.outstanding } : null}
        limits={limits}
        onClose={() => setAdjusting(null)}
        onSaved={refreshInvoice}
        onWaiveInstead={prefill => { setWaivePrefill(prefill); setWaiveOpen(true); }}
      />

      <FeeRequestDialog
        kind={requestKind}
        invoice={invoice ? { _id: invoice._id, invoiceNumber: formatChallanNumber(invoice.invoiceNumber) } : null}
        limits={limits}
        onClose={() => setRequestKind(null)}
        onSaved={refreshInvoice}
      />

      <ReasonDialog
        open={!!revoking}
        onClose={() => setRevoking(null)}
        title={revoking?.writeOff ? 'Undo this write-off?' : revoking?.type === 'waiver' ? 'Revoke waiver?' : 'Revoke adjustment?'}
        consequence={revoking?.writeOff
          ? 'The written-off balance becomes due again, so the family can pay it. The bad debt is reversed in the books.'
          : revoking?.type === 'waiver'
            ? 'Outstanding on this challan goes back up by that amount. The concession is reversed in the books.'
            : 'Outstanding drops (for a charge or late fee) or rises (for a reduction) by that amount, and the entry is reversed in the books.'}
        facts={revoking ? [{ label: revoking.type === 'waiver' ? 'Waiver' : 'Adjustment', value: revoking.label }] : []}
        confirmText={revoking?.writeOff ? 'Undo write-off' : 'Revoke'}
        confirmColor={revoking?.writeOff ? 'primary' : 'error'}
        reasonPlaceholder={revoking?.writeOff ? 'e.g. The family has agreed to pay' : 'Why is this being revoked?'}
        onConfirm={async reason => {
          if (!revoking || !invoiceId) return;
          if (revoking.type === 'waiver') await feeWaiverService.revokeWaiver(revoking.id, reason);
          else await feeInvoiceService.revokeAdjustment(invoiceId, revoking.id, reason);
          enqueueSnackbar(revoking.writeOff ? 'Write-off undone' : 'Revoked', { variant: 'success' });
          await refreshInvoice();
        }}
      />
    </Dialog>
  );
};

const Row: React.FC<{ label: string; value: string; bold?: boolean; muted?: boolean; color?: string }> = ({
  label, value, bold, muted, color,
}) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography variant="body2" color={muted ? 'text.disabled' : 'text.secondary'}>{label}</Typography>
    <Typography
      variant="body2"
      fontWeight={bold ? 700 : 500}
      sx={{ color: color, fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
  </Stack>
);

export default InvoiceDetailDialog;
